import { sha256HexSync } from "./sha256Sync";

const OVERRIDE_KEYS = new Set([
  "version",
  "status",
  "source_revision",
  "repeat",
  "weeks",
]);
const WEEK_KEYS = new Set(["id", "index", "label", "choices"]);
const STATUS_VALUES = new Set(["draft", "validated"]);
const SOURCE_REVISION_RE = /^four-week-v1-[0-9a-f]{64}$/;

function hasExactKeys(value, allowed) {
  return Object.keys(value).every((key) => allowed.has(key));
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, stableValue(value[key])]),
  );
}

function cleanText(value, maxLength) {
  if (typeof value !== "string") return null;
  const text = value.replace(/\s+/g, " ").trim();
  return text && text.length <= maxLength ? text : null;
}

function cleanId(value) {
  const id = cleanText(value, 120);
  return id && /^[a-z0-9][a-z0-9:_-]*$/i.test(id) ? id : null;
}

function cleanStringArray(value, maxItems, maxLength) {
  if (!Array.isArray(value) || value.length === 0 || value.length > maxItems) return null;
  const cleaned = value.map((item) => cleanText(item, maxLength));
  return cleaned.some((item) => !item) ? null : cleaned;
}

function normalizeRecipe(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const ingredients = cleanStringArray(value.ingredients, 80, 300);
  const preparation = cleanStringArray(value.preparation, 80, 500);
  if (!ingredients || !preparation) return null;
  if (value.tip !== undefined && !cleanText(value.tip, 500)) return null;
  // Couvrir l'objet effectivement rendu, y compris ses champs additifs, évite
  // qu'une projection normalisée partage son empreinte avec un contenu distinct.
  return stableValue(value);
}

function normalizeAlternative(value) {
  const id = cleanId(value?.id);
  const title = cleanText(value?.title, 180);
  const recipe = normalizeRecipe(value?.recipe);
  if (!id || id !== value?.id || id === "primary" || !title
      || (value?.recipe != null && !recipe)
      || (value?.hint !== undefined && !cleanText(value.hint, 300))) return null;
  return {
    id,
    title: value.title,
    hint: value?.hint === undefined ? null : value.hint,
    recipe,
  };
}

function normalizeMeal(value) {
  const id = cleanId(value?.id);
  const slot = cleanId(value?.slot);
  const title = cleanText(value?.title, 180);
  const recipe = normalizeRecipe(value?.recipe);
  if (!id || id !== value?.id || !slot || slot !== value?.slot || !title
      || (value?.recipe != null && !recipe)
      || (value?.slot_label !== undefined && !cleanText(value.slot_label, 80))
      || (value?.hint !== undefined && !cleanText(value.hint, 300))) return null;
  const alternatives = Array.isArray(value?.alternatives)
    ? value.alternatives.map(normalizeAlternative)
    : [];
  if (alternatives.some((alternative) => !alternative)) return null;
  const alternativeIds = new Set();
  if (alternatives.some((alternative) => {
    if (alternativeIds.has(alternative.id)) return true;
    alternativeIds.add(alternative.id);
    return false;
  })) return null;
  return {
    id,
    slot,
    slot_label: value?.slot_label === undefined ? slot : value.slot_label,
    title: value.title,
    hint: value?.hint === undefined ? null : value.hint,
    recipe,
    alternatives,
  };
}

function normalizeSource(weekMeals) {
  const days = Array.isArray(weekMeals?.days) ? weekMeals.days : [];
  const normalizedDays = days.map((day) => {
    const id = cleanId(day?.id);
    const index = Number(day?.index);
    const label = cleanText(day?.label, 80);
    const meals = Array.isArray(day?.meals) ? day.meals.map(normalizeMeal) : [];
    if (!id || id !== day?.id || !Number.isInteger(index) || index < 1 || index > 7 || !label
        || (day?.short_label !== undefined && !cleanText(day.short_label, 12))
        || meals.length === 0 || meals.some((meal) => !meal)) return null;
    return {
      id,
      index,
      label: day.label,
      short_label: day?.short_label === undefined ? null : day.short_label,
      meals,
    };
  });
  if (normalizedDays.length !== 7 || normalizedDays.some((day) => !day)) return null;
  const dayIds = new Set(normalizedDays.map((day) => day.id));
  const mealIds = normalizedDays.flatMap((day) => day.meals.map((meal) => meal.id));
  if (dayIds.size !== 7 || new Set(mealIds).size !== mealIds.length) return null;
  return {
    version: 1,
    structure: stableValue(weekMeals?.structure || null),
    days: normalizedDays,
  };
}

function detailedChoiceIds(meal) {
  const choices = [];
  if (meal.recipe) choices.push("primary");
  for (const alternative of meal.alternatives) {
    if (alternative.recipe) choices.push(alternative.id);
  }
  return choices;
}

function detailedDistinctChoiceIds(meal) {
  const seen = new Set();
  return detailedChoiceIds(meal).filter((choiceId) => {
    const selected = selectedVisibleChoice(meal, choiceId);
    if (!selected) return false;
    const signature = JSON.stringify(selected);
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function buildWeeksFromSource(source) {
  const allMeals = source.days.flatMap((day) => day.meals);
  const choicesByMeal = new Map(
    allMeals.map((meal) => [meal.id, detailedDistinctChoiceIds(meal)]),
  );
  if (Array.from(choicesByMeal.values()).some((choices) => choices.length < 4)) {
    throw new Error(
      "Rotation 4 semaines à actualiser : chaque menu doit proposer un choix principal et au moins 3 alternatives avec recette détaillée.",
    );
  }
  return Array.from({ length: 4 }, (_, weekOffset) => ({
    id: `week-${weekOffset + 1}`,
    index: weekOffset + 1,
    label: `Semaine ${weekOffset + 1}`,
    choices: Object.fromEntries(allMeals.map((meal) => {
      const candidates = choicesByMeal.get(meal.id);
      return [meal.id, candidates[weekOffset % candidates.length]];
    })),
  }));
}

export function buildFourWeekRotationSourceRevision(weekMeals) {
  const source = normalizeSource(weekMeals);
  if (!source) return null;
  return `four-week-v1-${sha256HexSync(JSON.stringify(stableValue(source)))}`;
}

export function createFourWeekRotationDraft(weekMeals) {
  const source = normalizeSource(weekMeals);
  if (!source) {
    throw new Error("La semaine de menus doit être complète avant de préparer les 4 semaines.");
  }
  const draft = {
    version: 1,
    status: "draft",
    source_revision: `four-week-v1-${sha256HexSync(JSON.stringify(stableValue(source)))}`,
    repeat: true,
    weeks: buildWeeksFromSource(source),
  };
  if (!hasFourDistinctResolvedWeeks(weekMeals, draft)) {
    throw new Error(INSUFFICIENT_ROTATION_ISSUE);
  }
  return draft;
}

/**
 * Ré-empreinte une rotation existante sur une nouvelle source sans perdre les
 * choix d'Anissa. Les choix ne sont conservés que s'ils restent valides et
 * donnent toujours quatre semaines distinctes ; sinon, proposition par défaut.
 */
export function refreshFourWeekRotationDraft(weekMeals, existing, options = {}) {
  const source = normalizeSource(weekMeals);
  if (!source) {
    throw new Error("La semaine de menus doit être complète avant de préparer les 4 semaines.");
  }
  const parsed = parseFourWeekRotationOverride(existing);
  if (!parsed) return createFourWeekRotationDraft(weekMeals);
  const fillMissing = options.fillMissing !== false;
  // Repas par repas : chaque choix encore valide est gardé ; seuls les choix
  // devenus invalides sont complétés par un choix détaillé pas encore utilisé.
  const meals = source.days.flatMap((day) => day.meals);
  const weeks = parsed.weeks.map((week) => ({ ...week, choices: { ...week.choices } }));
  let unresolved = false;
  for (const meal of meals) {
    const allowed = detailedDistinctChoiceIds(meal);
    const allowedSet = new Set(detailedChoiceIds(meal));
    // Un même plat (identifiant ou contenu) ne compte qu'une fois : la
    // première semaine le garde, les suivantes sont à compléter.
    const seenSignatures = new Set();
    const kept = weeks.map((week) => {
      const choiceId = week.choices[meal.id];
      if (!allowedSet.has(choiceId)) return null;
      const signature = JSON.stringify(selectedVisibleChoice(meal, choiceId));
      if (seenSignatures.has(signature)) return null;
      seenSignatures.add(signature);
      return choiceId;
    });
    const unused = allowed.filter((choiceId) => (
      !seenSignatures.has(JSON.stringify(selectedVisibleChoice(meal, choiceId)))
    ));
    weeks.forEach((week, index) => {
      if (kept[index]) {
        week.choices[meal.id] = kept[index];
      } else if (fillMissing && unused.length > 0) {
        week.choices[meal.id] = unused.shift();
      } else {
        // Jamais de remplissage silencieux : la semaine reste « à choisir ».
        week.choices[meal.id] = MISSING_CHOICE_ID;
        unresolved = true;
      }
    });
  }
  if (unresolved) {
    if (fillMissing) throw new Error(INSUFFICIENT_ROTATION_ISSUE);
  } else if (!validateWeeksForSource(source, weeks) || !hasFourDistinctResolvedChoicesPerMeal(source, weeks)) {
    throw new Error("Rotation 4 semaines à vérifier : choisis à nouveau le plat principal des semaines concernées.");
  }
  return {
    version: 1,
    status: "draft",
    source_revision: `four-week-v1-${sha256HexSync(JSON.stringify(stableValue(source)))}`,
    repeat: true,
    weeks,
  };
}

export function parseFourWeekRotationOverride(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!hasExactKeys(value, OVERRIDE_KEYS)
      || value.version !== 1
      || !STATUS_VALUES.has(value.status)
      || value.repeat !== true
      || typeof value.source_revision !== "string"
      || !SOURCE_REVISION_RE.test(value.source_revision)
      || !Array.isArray(value.weeks)
      || value.weeks.length !== 4) return null;
  const weeks = value.weeks.map((week, offset) => {
    if (!week || typeof week !== "object" || Array.isArray(week)
        || !hasExactKeys(week, WEEK_KEYS)
        || week.id !== `week-${offset + 1}`
        || week.index !== offset + 1
        || week.label !== `Semaine ${offset + 1}`
        || !week.choices || typeof week.choices !== "object" || Array.isArray(week.choices)) {
      return null;
    }
    const choices = {};
    for (const [mealId, choiceId] of Object.entries(week.choices)) {
      const cleanMealId = cleanId(mealId);
      const cleanChoiceId = choiceId === "primary" ? "primary" : cleanId(choiceId);
      if (!cleanMealId || !cleanChoiceId) return null;
      choices[cleanMealId] = cleanChoiceId;
    }
    return { id: week.id, index: week.index, label: week.label, choices };
  });
  if (weeks.some((week) => !week)) return null;
  return {
    version: 1,
    status: value.status,
    source_revision: value.source_revision,
    repeat: true,
    weeks,
  };
}

function validateWeeksForSource(source, weeks) {
  if (!Array.isArray(weeks) || weeks.length !== 4) return false;
  const meals = source.days.flatMap((day) => day.meals);
  const expectedMealIds = meals.map((meal) => meal.id).sort();
  const allowedByMeal = new Map(meals.map((meal) => [meal.id, new Set(detailedChoiceIds(meal))]));
  return weeks.every((week, offset) => {
    if (week?.id !== `week-${offset + 1}`
        || week?.index !== offset + 1
        || week?.label !== `Semaine ${offset + 1}`
        || !week.choices || typeof week.choices !== "object" || Array.isArray(week.choices)) {
      return false;
    }
    const choiceMealIds = Object.keys(week.choices).sort();
    return JSON.stringify(choiceMealIds) === JSON.stringify(expectedMealIds)
      && choiceMealIds.every((mealId) => allowedByMeal.get(mealId)?.has(week.choices[mealId]));
  });
}

function selectedVisibleChoice(meal, choiceId) {
  const selected = choiceId === "primary"
    ? meal
    : meal.alternatives.find((alternative) => alternative.id === choiceId);
  if (!selected || !selected.recipe) return null;
  return stableValue({
    title: selected.title,
    hint: selected.hint,
    recipe: selected.recipe,
  });
}

function hasFourDistinctResolvedChoicesPerMeal(source, weeks) {
  if (!Array.isArray(weeks) || weeks.length !== 4) return false;
  return source.days.every((day) => day.meals.every((meal) => {
    const signatures = weeks.map((week) => {
      const selected = selectedVisibleChoice(meal, week?.choices?.[meal.id]);
      return selected ? JSON.stringify(selected) : null;
    });
    return signatures.every(Boolean) && new Set(signatures).size === 4;
  }));
}

export function hasFourDistinctResolvedWeeks(weekMeals, value) {
  const parsed = parseFourWeekRotationOverride(value);
  const source = normalizeSource(weekMeals);
  if (!parsed || !source || !validateWeeksForSource(source, parsed.weeks)) return false;
  return hasFourDistinctResolvedChoicesPerMeal(source, parsed.weeks);
}

/**
 * Marqueur « à choisir » : accepté par le format existant, jamais produit par
 * un identifiant d'alternative (slug sans double tiret). Une semaine qui le
 * porte n'est pas publiable tant qu'Anissa n'a pas choisi.
 */
export const MISSING_CHOICE_ID = "choix--a-choisir";

const INSUFFICIENT_ROTATION_ISSUE ="Les 4 semaines doivent être toutes différentes. Ajoute ou génère au moins 3 alternatives détaillées par menu, puis actualise le cycle avant de le valider.";

export function getFourWeekRotationPublicationIssue(weekMeals, value) {
  if (!value) return null;
  const parsed = parseFourWeekRotationOverride(value);
  if (!parsed) return "Rotation 4 semaines à vérifier";
  let source;
  try {
    source = normalizeSource(weekMeals);
    if (!source) throw new Error("Rotation 4 semaines à vérifier");
    buildWeeksFromSource(source);
  } catch (error) {
    return error?.message || "Rotation 4 semaines à vérifier";
  }
  if (parsed.source_revision !== buildFourWeekRotationSourceRevision(weekMeals)
      || !validateWeeksForSource(source, parsed.weeks)) {
    return "Rotation 4 semaines à actualiser après les modifications des menus ou recettes";
  }
  if (!hasFourDistinctResolvedWeeks(weekMeals, parsed)) {
    return INSUFFICIENT_ROTATION_ISSUE;
  }
  if (parsed.status !== "validated") {
    return "Rotation 4 semaines préparée mais pas encore validée par Anissa";
  }
  return null;
}

export function validateFourWeekRotation(weekMeals, value) {
  const parsed = parseFourWeekRotationOverride(value);
  if (!parsed) throw new Error("Rotation 4 semaines à vérifier");
  const source = normalizeSource(weekMeals);
  if (!source
      || parsed.source_revision !== buildFourWeekRotationSourceRevision(weekMeals)
      || !validateWeeksForSource(source, parsed.weeks)) {
    throw new Error("Actualise les 4 semaines après les modifications des menus ou recettes.");
  }
  if (!hasFourDistinctResolvedWeeks(weekMeals, parsed)) {
    throw new Error(INSUFFICIENT_ROTATION_ISSUE);
  }
  return { ...parsed, status: "validated" };
}

export function buildPublishedFourWeekRotation(weekMeals, value) {
  if (!value) return null;
  if (getFourWeekRotationPublicationIssue(weekMeals, value)) return null;
  const parsed = parseFourWeekRotationOverride(value);
  return {
    version: 1,
    repeat: true,
    weeks: parsed.weeks,
  };
}

/**
 * Matérialise temporairement les choix du cycle pour les seules fonctions
 * logistiques. Le DTO client reste compact et ne duplique jamais les 28 jours.
 */
export function resolveFourWeekRotationDays(weekMeals) {
  const days = Array.isArray(weekMeals?.days) ? weekMeals.days : [];
  const rotation = weekMeals?.rotation;
  if (!rotation) return days;
  const source = normalizeSource(weekMeals);
  if (!source) throw new Error("Rotation 4 semaines à vérifier");
  const allowedKeys = new Set(["version", "repeat", "weeks"]);
  if (!rotation || typeof rotation !== "object" || Array.isArray(rotation)
      || Object.keys(rotation).some((key) => !allowedKeys.has(key))
      || rotation.version !== 1 || rotation.repeat !== true
      || !validateWeeksForSource(source, rotation.weeks)
      || !hasFourDistinctResolvedChoicesPerMeal(source, rotation.weeks)) {
    throw new Error("Rotation 4 semaines à vérifier");
  }
  return rotation.weeks.flatMap((week) => days.map((day) => ({
    ...day,
    id: `${week.id}:${day.id}`,
    label: `${week.label} · ${day.label}`,
    source_day_id: day.id,
    source_day_label: day.label,
    meals: (day.meals || []).map((meal) => {
      const choiceId = week.choices[meal.id];
      const selected = choiceId === "primary"
        ? meal
        : meal.alternatives?.find((alternative) => alternative.id === choiceId);
      if (!selected || !normalizeRecipe(selected.recipe)) {
        throw new Error("Rotation 4 semaines à vérifier");
      }
      return {
        ...meal,
        id: `${week.id}:${meal.id}`,
        source_meal_id: meal.id,
        title: selected.title,
        ...(selected.hint ? { hint: selected.hint } : {}),
        recipe: selected.recipe,
      };
    }),
  })));
}
